import React from 'react';
import { Box, Button, Stack, TextField } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { http, unwrapApiResponse } from '../api/http';
import { PageHeader } from '../components/PageHeader';
import { ErrorBanner } from '../components/ErrorBanner';
import { RecordDialog } from '../components/RecordDialog';
import { useAppUi } from '../app/useAppUi';

export function SearchHistoryPage() {
  const { t } = useAppUi();
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [rows, setRows] = React.useState([]);
  const [rowCount, setRowCount] = React.useState(0);
  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(20);
  const [userId, setUserId] = React.useState('');
  const [selected, setSelected] = React.useState(null);
  const [viewOpen, setViewOpen] = React.useState(false);

  const fetchData = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await http.get('/admin/search-history', {
        params: {
          take: pageSize,
          page: page + 1,
          user_id: userId ? Number(userId) : undefined,
        },
      });
      const data = unwrapApiResponse(res);
      setRows(data?.history || []);
      setRowCount(data?.total || 0);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, userId]);

  React.useEffect(() => {
    fetchData();
  }, [fetchData]);

  const columns = React.useMemo(
    () => [
      { field: 'id', headerName: t('searchHistory.colId'), width: 90 },
      { field: 'user_id', headerName: t('searchHistory.colUserId'), width: 110 },
      { field: 'user_name', headerName: t('searchHistory.colUser'), width: 180 },
      { field: 'agency_name', headerName: t('searchHistory.colAgency'), width: 180 },
      { field: 'phone_number', headerName: t('searchHistory.colPhone'), width: 150 },
      { field: 'country', headerName: t('searchHistory.colCountry'), width: 140 },
      { field: 'city', headerName: t('searchHistory.colCity'), width: 140 },
      { field: 'date_from', headerName: t('searchHistory.colFrom'), width: 120 },
      { field: 'date_to', headerName: t('searchHistory.colTo'), width: 120 },
      { field: 'nights', headerName: t('searchHistory.colNights'), width: 90 },
      { field: 'adults', headerName: t('searchHistory.colAdults'), width: 90 },
      { field: 'kids', headerName: t('searchHistory.colKids'), width: 90 },
      { field: 'created_at', headerName: t('searchHistory.colCreated'), width: 180 },
      {
        field: 'actions',
        headerName: t('searchHistory.colActions'),
        width: 120,
        sortable: false,
        filterable: false,
        renderCell: (p) => (
          <Button
            size="small"
            variant="text"
            onClick={() => {
              setSelected(p.row);
              setViewOpen(true);
            }}
          >
            {t('common.view')}
          </Button>
        ),
      },
    ],
    [t]
  );

  return (
    <Box>
      <PageHeader
        title={t('searchHistory.title')}
        subtitle={t('searchHistory.subtitle')}
        right={
          <Button variant="outlined" onClick={fetchData} disabled={loading}>
            {t('common.refresh')}
          </Button>
        }
      />

      <ErrorBanner error={error} />

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} sx={{ mb: 2 }}>
        <TextField
          label={t('searchHistory.filterUserId')}
          value={userId}
          onChange={(e) => setUserId(e.target.value)}
          size="small"
          sx={{ width: { xs: '100%', md: 280 } }}
        />
      </Stack>

      <Box sx={{ height: 640, bgcolor: 'background.paper', borderRadius: 2, boxShadow: (th) => th.custom.shadow, overflow: 'hidden' }}>
        <DataGrid
          rows={rows}
          columns={columns}
          loading={loading}
          paginationMode="server"
          rowCount={rowCount}
          density="compact"
          pageSizeOptions={[10, 20, 50, 100]}
          paginationModel={{ page, pageSize }}
          onPaginationModelChange={(m) => {
            setPage(m.page);
            setPageSize(m.pageSize);
          }}
          disableRowSelectionOnClick
        />
      </Box>

      <RecordDialog
        open={viewOpen}
        title={t('searchHistory.dialogTitle', { id: selected?.id || '' })}
        record={selected}
        onClose={() => setViewOpen(false)}
      />
    </Box>
  );
}

